import React, { useState, useEffect, useRef } from 'react';
import {
  AppBar, Toolbar, IconButton, Typography, TextField, Button, Container, Paper, CircularProgress, Box, Avatar, 
  Tooltip, Menu, MenuItem, Drawer, List, ListItem, ListItemIcon, ListItemText, Divider, 
  Select,
  FormControl,
} from '@mui/material';
import {
  Menu as MenuIcon, Send as SendIcon, UploadFile as UploadFileIcon, Logout as LogoutIcon,
  MoreVert as MoreVertIcon, Edit as EditIcon, Reviews as ReviewsIcon,
  AccountBox as ProfileIcon, ContentCopy as ContentCopyIcon, AutoAwesome as AutoAwesomeIcon,
  Loop as LoopIcon, Dashboard as DashboardIcon
} from '@mui/icons-material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import './App.css';

const App = () => {
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [menuMessage, setMenuMessage] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [user, setUser] = useState(null);
  const chatEndRef = useRef(null);
  const [avatarAnchorEl, setAvatarAnchorEl] = useState(null);
  const [fileCount, setFileCount] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [model, setModel] = useState('gpt-4o');
  const [currentModel, setCurrentModel] = useState('gpt-4o');

  const handleSendMessage = async () => {
    if (userInput.trim() === '') return;

    const newMessage = { role: 'user', content: userInput };
    setMessages((prevMessages) => [...prevMessages, newMessage]);
    setUserInput('');
    setLoading(true);

    try {
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ message: userInput, chat_history: messages, model }),
      });
      const data = await response.json();
      setMessages((prevMessages) => [
        ...prevMessages, { role: 'assistant', content: data.response }]);
      setLoading(false);
      setCurrentModel(data.model);
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages((prevMessages) => [
        ...prevMessages,
        { role: 'assistant', content: 'Error sending message.' },
      ]);
    }

    setLoading(false);
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const handleFileUpload = async (event) => {
    const files = event.target.files;
    if (files.length === 0) return;

    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append('files', files[i]);
    }

    try {
      const response = await fetch('/api/upload', {
        method: 'POST',
        body: formData,
      });
      const data = await response.json();
      console.log(data);
      setFileCount(files.length);
      setMessages((prevMessages) => [
        ...prevMessages,
        { role: 'system', content: `${files.length} file(s) uploaded successfully.` },
      ]);
    } catch (error) {
      console.error('Error uploading files:', error);
      setMessages((prevMessages) => [
        ...prevMessages,
        { role: 'system', content: 'Error uploading files.' },
      ]);
    }
  };

  const handleAvatarMenuClick = (event) => {
    setAvatarAnchorEl(event.currentTarget);
  };

  const handleAvatarMenuClose = () => {
    setAvatarAnchorEl(null);
  };

  const handleCopy = (content) => {
    navigator.clipboard.writeText(content);
    alert('Copied');
    handleMenuClose();
  };

  const handleMenuOpen = (event, message) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuMessage(message);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuMessage(null);
  };

  const handleEdit = () => {
    setUserInput(menuMessage.content);
    setMessages((prevMessages) => prevMessages.filter((msg) => msg !== menuMessage));
    setIsEditing(true);
    handleMenuClose();
  };

  const handleRegenerate = async (message) => {
    if (message.role === 'assistant') {
      setLoading(true);
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ message: message.content, chat_history: messages, model }),
      });
      const data = await response.json();

      const updatedMessages = [...messages, { role: 'assistant', content: data.response }];
      setMessages(updatedMessages);
      setLoading(false);
    }
    handleMenuClose();
  };

  const checkUser = async () => {
    try {
      const response = await fetch('/api/logged_in', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      
      });
      if (response.ok) {
        const data = await response.json();
        console.log(data);
        setUser({ sso: data.sso });
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error("Error fetching user sso:", error);
      setUser(null);
    }
  };

  // Uncomment only for testing purpose with hardcoded credentials 
  // const handleLogin = async (username, password) => {
  //   const response = await fetch('/api/login', {
  //     method: 'POST',
  //     headers: {
  //       'Content-Type': 'application/json',
  //     },
  //     body: JSON.stringify({ username, password }),
  //   });
  //   const data = await response.json();
  //   if (response.ok) {
  //     setUser({ username: data.username });
  //   } else {
  //     alert(data.message);
  //   }
  
  const handleLogin = async () => { 
    window.location.href = '/api/login';
  };

  const handleLogout = async () => {
    const response = await fetch('/api/logout', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if (response.ok) {
      setUser(null);
      setMessages([]);
    } else {
      alert('Logout failed');
    }
  };

  const handleModelChange = (event) => {
    setModel(event.target.value);
  };

  const toggleDrawer = (open) => (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    setDrawerOpen(open);
  };

  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, loading]);

  useEffect(() => {
    checkUser();
  }, []);

  if (!user) {
    return (
      <Container className='login-container' maxWidth="sm">
        <Box mt={5} textAlign="center">
          <Typography variant="h4" gutterBottom>
            Talent Acquisition Inventory
          </Typography>
          {/* Uncomment only for testing purpose with hardcoded credentials */}
          {/* <Box component="form" onSubmit={(e) => { e.preventDefault(); handleLogin(e.target.username.value, e.target.password.value); }}>
            <TextField required label="Username" name="username" fullWidth margin="normal" />
            <TextField required label="Password" name="password" type="password" fullWidth margin="normal" />
            <Button variant="contained" color="secondary" type="submit">Login</Button>
          </Box> */}
          <Box>
            <Button variant="contained" color="primary" onClick={() => { handleLogin();
              }}> Login with OKTA</Button>
          </Box>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" className="chat-container">
      <AppBar position="static" className="app-bar">
        <Toolbar>
          <IconButton edge="start" color="inherit" aria-label="menu" onClick={toggleDrawer(true)}>
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" className="app-title">Evaluator AI</Typography>
          
          <FormControl variant="standard" className="model-select">
            <Select
              value={model}
              onChange={handleModelChange}
              // onChange={(e) => setModel(e.target.value)}
              label="Model"
              startAdornment={<AutoAwesomeIcon/>}
            >
              <MenuItem value="gpt-4o">GPT-4o</MenuItem>
              <MenuItem value="gpt-4-turbo">GPT-4 Turbo</MenuItem>
              <MenuItem value="gpt-4">GPT-4</MenuItem>
              <MenuItem value="gpt-3.5-turbo-0125">GPT-3.5 Turbo</MenuItem>
            </Select>
            <Typography variant="caption" className="current-model">Active Model: {currentModel}</Typography>
          </FormControl>
          <div>
            <IconButton color="inherit" onClick={handleAvatarMenuClick}>
              {/* TODO */}
              <Tooltip title={"Logged in as: " + user.sso}>
                <Avatar alt={user.sso} src="">
                </Avatar>
              </Tooltip>
            </IconButton>
            <Menu anchorEl={avatarAnchorEl} open={Boolean(avatarAnchorEl)} onClose={handleAvatarMenuClose}>
              {/* TODO */}
              <MenuItem>
                <ListItemIcon><ProfileIcon /></ListItemIcon>
                {user.sso}
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <ListItemIcon><LogoutIcon /></ListItemIcon>
                Logout
              </MenuItem>
            </Menu>
          </div>
        </Toolbar>
      </AppBar>

      <Drawer anchor="left" open={drawerOpen} onClose={toggleDrawer(false)}>
        <div role="presentation" onClick={toggleDrawer(false)} onKeyDown={toggleDrawer(false)}>
          <List>
            {['Dashboard', 'Evaluator.AI'].map((text, index) => (
              <ListItem button key={text}>
                <ListItemIcon>{index % 2 === 0 ? <DashboardIcon /> : <ReviewsIcon />}</ListItemIcon>
                <ListItemText primary={text} />
              </ListItem>
            ))}
          </List>
          <Divider />
        </div>
      </Drawer>

      <Box className="chat-box">
        {messages.map((message, index) => (
          <Box key={index} className={`message-box ${message.role}`}>
            <Paper
              key={index}
              className={`message ${message.role === 'user' ? 'user-message' : 'assistant-message'}`}
              elevation={3}
            >
              <Box className="message-content">

                <Avatar className="avatar">
                  {message.role === 'user' ? <AccountCircleIcon /> : <SupportAgentIcon />}
                </Avatar>

                <Typography variant="body1" className="message-text">
                  {message.content.split('\n').map((text, i) => (
                      <React.Fragment key={i}>
                        {text.includes('*') ? (
                          <span className="message-bold">{text.replace(/\*/g, '')}</span>
                        ) : text.includes('_') ? (
                          <span className="message-italic">{text.replace(/_/g, '')}</span>
                        ) : text.includes('-') ? (
                          <span className="message-bullet">{text.replace(/-/g, '')}</span>
                        ) : (
                          <span className="message-normal">{text}</span>
                        )}
                        <br />
                      </React.Fragment>
                    ))}
                </Typography>
                <IconButton onClick={(e) => handleMenuOpen(e, message)}>
                    <MoreVertIcon />
                  </IconButton>
              </Box>
            </Paper>
          </Box>
        ))}
        {loading && <CircularProgress className="loader" />}
        <div ref={chatEndRef} />
      </Box>

      <Box className="input-box">
        <input
          accept=".pdf,.docx"
          style={{ display: 'none' }}
          id="file-upload"
          type="file"
          multiple
          onChange={handleFileUpload}
        />
        <label htmlFor="file-upload">
          <IconButton component="span">
            <UploadFileIcon />
          </IconButton>
        </label>

        <TextField
          fullWidth
          variant="outlined"
          value={userInput}
          onChange={(e) => setUserInput(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Type your message..."
        />
        
        <IconButton color='primary' onClick={handleSendMessage}>
          <SendIcon />
        </IconButton>
      </Box>
      <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleMenuClose}>
        {menuMessage ? (
          <>
            {menuMessage.role === 'user' ? (
              <>
                <MenuItem onClick={handleEdit}>
                  <ListItemIcon>
                    <EditIcon />
                  </ListItemIcon>
                  Edit
                </MenuItem>
                <MenuItem onClick={() => handleCopy(menuMessage.content)}>
                  <ListItemIcon>
                    <ContentCopyIcon />
                  </ListItemIcon>
                  Copy
                </MenuItem>
              </>
            ) : (
              <>
                <MenuItem onClick={() => handleCopy(menuMessage.content)}>
                  <ListItemIcon>
                    <ContentCopyIcon />
                  </ListItemIcon>
                  Copy
                </MenuItem>
                <MenuItem onClick={() => handleRegenerate(menuMessage)}>
                  <ListItemIcon>
                    <LoopIcon />
                  </ListItemIcon>
                  Regenerate
                </MenuItem>
              </>
            )}
          </>
        ) : null}
      </Menu>
    </Container>
  );
};

export default App;

